import { APIError } from './APIError';

export type SignInResult = {
	accessToken?: string;
	idToken?: string;
	refreshToken?: string;
	username?: string;
	challengeName?: string;
	session?: string;
	codeDeliveryDestination?: string;
	codeDeliveryMedium?: string;
};

type SignInResponseDTO = {
	access_token?: string;
	id_token?: string;
	refresh_token?: string;
	username?: string;
	challenge_name?: string;
	session?: string;
	code_delivery_destination?: string;
	code_delivery_medium?: string;
};

const apiBase = (): string =>
	process.env.GATSBY_FOLX_API_V2_URL ?? 'https://apiv2.integration.folxhealth.net';

const toSignInResult = (dto: SignInResponseDTO): SignInResult => ({
	accessToken: dto.access_token,
	idToken: dto.id_token,
	refreshToken: dto.refresh_token,
	username: dto.username,
	challengeName: dto.challenge_name,
	session: dto.session,
	codeDeliveryDestination: dto.code_delivery_destination,
	codeDeliveryMedium: dto.code_delivery_medium,
});

export const signIn = async (
	email: string,
	password: string,
	abort?: AbortSignal
): Promise<SignInResult> => {
	const resp = await fetch(`${apiBase()}/api/v1/signin`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			email,
			password,
			client_id: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}

	const dto: SignInResponseDTO = await resp.json();

	return toSignInResult(dto);
};

export const verifyMfa = async (
	username: string,
	session: string,
	challengeName: string,
	code: string,
	abort?: AbortSignal
): Promise<SignInResult> => {
	const resp = await fetch(`${apiBase()}/api/v1/signin/mfa`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			username,
			session,
			challenge_name: challengeName,
			code,
			client_id: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}

	const dto: SignInResponseDTO = await resp.json();

	return toSignInResult(dto);
};

export type RefreshResult = {
	accessToken?: string;
	idToken?: string;
};

export const refresh = async (
	refreshToken: string,
	abort?: AbortSignal
): Promise<RefreshResult> => {
	const resp = await fetch(`${apiBase()}/api/v1/refresh`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			refresh_token: refreshToken,
			client_id: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}

	const dto: SignInResponseDTO = await resp.json();

	return {
		accessToken: dto.access_token,
		idToken: dto.id_token,
	};
};

export const signOut = async (refreshToken: string, abort?: AbortSignal): Promise<void> => {
	const resp = await fetch(`${apiBase()}/api/v1/signout`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			refresh_token: refreshToken,
			client_id: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}
};

export const beginResetPassword = async (email: string, abort?: AbortSignal) => {
	const resp = await fetch(`${apiBase()}/api/v1/reset-password`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			email,
			client_id: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}
};

export const completeResetPassword = async (
	email: string,
	confirmation_code: string,
	new_password: string,
	abort?: AbortSignal
) => {
	const resp = await fetch(`${apiBase()}/api/v1/reset-password/confirm`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			email,
			confirmation_code,
			new_password,
			client_id: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}
};

export const enrollSmsMfa = async (
	authToken: string,
	accessToken: string,
	phoneNumber: string,
	abort?: AbortSignal
): Promise<void> => {
	const resp = await fetch(`${apiBase()}/api/v1/mfa/enroll/sms`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			authorizationToken: authToken,
		},
		body: JSON.stringify({
			access_token: accessToken,
			phone_number: phoneNumber,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}
};

export const confirmEnrollSmsMfa = async (
	authToken: string,
	accessToken: string,
	code: string,
	abort?: AbortSignal
): Promise<void> => {
	const resp = await fetch(`${apiBase()}/api/v1/mfa/enroll/sms/confirm`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			authorizationToken: authToken,
		},
		body: JSON.stringify({
			access_token: accessToken,
			code,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}
};

export type BeginEnrollTotpMfaResult = {
	secretCode: string;
};

type BeginEnrollTotpMfaResponseDTO = {
	secret_code?: string;
};

export const enrollTotpMfa = async (
	authToken: string,
	accessToken: string,
	abort?: AbortSignal
): Promise<BeginEnrollTotpMfaResult> => {
	const resp = await fetch(`${apiBase()}/api/v1/mfa/enroll/totp`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			authorizationToken: authToken,
		},
		body: JSON.stringify({
			access_token: accessToken,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}

	const dto: BeginEnrollTotpMfaResponseDTO = await resp.json();

	return { secretCode: dto.secret_code ?? '' };
};

export const confirmEnrollTotpMfa = async (
	authToken: string,
	accessToken: string,
	code: string,
	abort?: AbortSignal
): Promise<void> => {
	const resp = await fetch(`${apiBase()}/api/v1/mfa/enroll/totp/confirm`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			authorizationToken: authToken,
		},
		body: JSON.stringify({
			access_token: accessToken,
			code,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}
};

export const enableEmailMfa = async (
	authToken: string,
	accessToken: string,
	abort?: AbortSignal
): Promise<void> => {
	const resp = await fetch(`${apiBase()}/api/v1/mfa/enroll/email`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
			authorizationToken: authToken,
		},
		body: JSON.stringify({
			access_token: accessToken,
		}),
		signal: abort,
	});

	if (!resp.ok) {
		throw new APIError(resp.status, await resp.text());
	}
};
